import React, { useState, useMemo, useCallback, memo } from 'react';
import Select from 'react-select';
import { CATEGORIESYAHOO, CategoryYahoo, searchYahooCategories } from '../../utils/categories';
import clsx from 'clsx';

const selectStyles = {
  control: {
    base: "flex border bg-gray-50 rounded-md bg-input hover:cursor-pointer min-w-[100px] !min-h-[40px] w-[100%] h-[100%] text-foreground",
    focus: "border border-primary shadow-[0_0_0_2px_rgba(var(--primary),0.3)]",
    nonFocus: "border-border"
  },
  option: {
    base: "hover:cursor-pointer px-3 py-2 rounded flex gap-2 items-center hover:bg-muted !text-sm",
    focus: "",
    selected: "bg-[rgba(var(--primary),0.15)] hover:bg-[rgba(var(--primary),0.15)] text-primary font-bold"
  }
};

const classNames = {
  control: ({ isFocused, hasValue }: any) =>
    clsx(
      isFocused ? selectStyles.control.focus : selectStyles.control.nonFocus,
      selectStyles.control.base,
      // Add conditional styling based on whether a category is selected
      hasValue 
        ? 'bg-blue-200 text-blue-600 hover:bg-blue-100 shadow-md hover:shadow-lg border-blue-500' 
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    ),
  option: ({ isSelected, isFocused }: any) =>
    clsx(
      selectStyles.option.base,
      isSelected ? selectStyles.option.selected : "",
      isFocused ? selectStyles.option.focus : ""
    ),
  menu: () => "p-1 mt-1 bg-card rounded-md shadow-md bg-gray-100",
  placeholder: () => "text-current pl-1 text-sm opacity-70",
  input: () => "text-foreground pl-1",
  valueContainer: () => "p-1 gap-1",
  singleValue: () => "pl-1 text-current font-medium",
  multiValue: () => "flex bg-[rgba(var(--primary),0.15)] p-0 pl-2 rounded-sm text-xs overflow-hidden",
  multiValueLabel: () => "py-1",
  multiValueRemove: () => "bg-[rgba(var(--primary),0.1)] hover:bg-[rgba(var(--primary),0.2)] text-primary px-1 ml-2",
  indicatorsContainer: () => "p-1 gap-1",
  clearIndicator: () => "text-muted-foreground px-1 hover:text-error",
  indicatorSeparator: () => "bg-border",
  dropdownIndicator: () => "p-1 hover:bg-muted text-muted-foreground rounded-md hover:text-foreground",
  groupHeading: () => "ml-3 mt-2 mb-1 text-muted-foreground text-sm",
  noOptionsMessage: () => "text-placeholder py-3"
};

const FormatOptionLabel = memo(({ option }: { option: CategoryYahoo }) => (
  <><div>{option.label}</div><div className='text-xs'>{option.path}</div></>
));

// Maximum number of category history items to store
const MAX_HISTORY_ITEMS = 5;

// Local storage key for category history
const CATEGORY_HISTORY_KEY = 'selectedCategoryHistory';

interface CategorySelectorAutoProps {
  valueYahoo?: string;
  onChangeYahoo?: (value: any) => void;
}

export function CategorySelectorAuto({ valueYahoo, onChangeYahoo }: CategorySelectorAutoProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [categoryHistory, setCategoryHistory] = useState<CategoryYahoo[]>(() => {
    try {
      const savedHistory = localStorage.getItem(CATEGORY_HISTORY_KEY);
      return savedHistory ? JSON.parse(savedHistory) : [];
    } catch (error) {
      console.error('Failed to load category history:', error);
      return [];
    }
  });
  
  // Debounce search input
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(searchInput);
    }, 300);
    
    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);
  
  // Memoize filtered options
  const filteredOptions = useMemo(() => {
    if (debouncedInput.length < 1) return [];
    
    // Process input to handle both comma and space separators
    let searchTerms: string[] = [];
    
    // Check if input contains commas
    if (debouncedInput.includes(',')) {
      // Use comma as separator
      searchTerms = debouncedInput.toLowerCase().split(',').map(term => term.trim()).filter(Boolean);
    } else {
      // Use space as separator
      searchTerms = debouncedInput.toLowerCase().split(/\s+/).filter(Boolean);
    }
    
    if (searchTerms.length === 0) return [];
    
    // Sort results by length of the matching term
    const results = CATEGORIESYAHOO.filter(option => {
      const path = String(option.path || '').toLowerCase();
      const label = String(option.label || '').toLowerCase();
      
      if (searchTerms.length > 1) {
        // AND search with multiple terms
        return searchTerms.every(term => path.includes(term) || label.includes(term));
      } else {
        // Single term search
        const term = searchTerms[0];
        return path.includes(term) || label.includes(term);
      }
    });
    
    // Sort results by total character length (label + path)
    // This prioritizes categories with shorter total text length
    return results
      .sort((a, b) => {
        const aLabel = String(a.label || '');
        const bLabel = String(b.label || '');
        const aPath = String(a.path || '');
        const bPath = String(b.path || '');
        
        // Calculate total length of text
        const aTotalLength = aLabel.length + aPath.length;
        const bTotalLength = bLabel.length + bPath.length;
        
        // Sort by total length (shorter first)
        return aTotalLength - bTotalLength;
      })
      .slice(0, 100); // Limit results to improve performance
  }, [debouncedInput, CATEGORIESYAHOO]);
  
  // Memoize selected category option
  const selectedCategoryOption = useMemo(() => {
    return CATEGORIESYAHOO.find(opt => String(opt.value) === String(valueYahoo)) || null;
  }, [valueYahoo, CATEGORIESYAHOO]);
  
  const handleInputChange = useCallback((input: string) => {
    setSearchInput(input);
  }, []);
  
  const handleChange = useCallback((selectedOption: CategoryYahoo | null) => {
    if (onChangeYahoo) {
      onChangeYahoo(selectedOption);
    }
    
    // Add to category history if not already in history
    if (selectedOption) {
      setCategoryHistory(prevHistory => {
        // Remove if already exists (to move to top)
        const filteredHistory = prevHistory.filter(item => item.value !== selectedOption.value);
        // Add to beginning and limit size
        const newHistory = [selectedOption, ...filteredHistory].slice(0, MAX_HISTORY_ITEMS);
        
        // Save to localStorage
        try {
          localStorage.setItem(CATEGORY_HISTORY_KEY, JSON.stringify(newHistory));
        } catch (error) {
          console.error('Failed to save category history:', error);
        }
        
        return newHistory;
      });
    }
  }, [onChangeYahoo]);
  
  const noOptionsMessage = useCallback(({ inputValue }: { inputValue: string }) => {
    if (inputValue.length < 1) {
      return (
        <div>
          <div className="text-sm font-medium">検索には１文字以上入力してください</div>
          {categoryHistory.length > 0 && (
            <div className="mt-2 border-t pt-2">
              <div className="text-sm font-medium text-gray-500 mb-1">最近選択したカテゴリ</div>
              <div className="space-y-1">
                {categoryHistory.map((category, index) => (
                  <div 
                    key={index}
                    className="px-2 py-1.5 text-blue-600 hover:bg-blue-50 rounded cursor-pointer"
                    onClick={() => handleChange(category)}
                  >
                    <div className="text-base font-medium">{category.label}</div>
                    <div className="text-xs text-gray-500">{category.path}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    return "カテゴリが見つかりません";
  }, [categoryHistory, handleChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <h4 className="font-medium mb-4">カテゴリ</h4>
      <Select
        unstyled
        name="categoryYahoo"
        options={filteredOptions}
        formatOptionLabel={(option) => (
          <FormatOptionLabel option={option} />
        )}
        classNames={classNames}
        placeholder="カテゴリを入力"
        noOptionsMessage={noOptionsMessage}
        isClearable={true}
        value={selectedCategoryOption}
        onChange={handleChange}
        onInputChange={handleInputChange}
        isSearchable={true}
        filterOption={() => true} // Disable built-in filtering
        isLoading={debouncedInput.length >= 1 && filteredOptions.length === 0}
        onFocus={handleFocus}
        onBlur={handleBlur}
        components={{
          DropdownIndicator: () => (
            <div className="p-1 text-gray-400">
              {isFocused && !searchInput && (
                <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-md z-10">
                  <div className="text-sm font-medium text-blue-600">検索には１文字以上入力してください</div>
                </div>
              )}
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          )
        }}
      />
      
      <div className="mt-4">
        <h4 className="font-medium mb-2">検索のヒント</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• 1文字以上入力すると検索が始まります</li>
          <li>• カンマまたはスペースで複数のキーワードを指定できます (例: 本 小説 or 本,小説)</li>
          <li>• 複数キーワードはAND検索になります (すべてのキーワードを含むカテゴリを検索)</li>
          <li>• 検索結果は文字数の少ない順に表示されます</li>
          <li>• カテゴリ名とパスの両方から検索されます</li>
        </ul>
      </div>
    </div>
  );
}